import { createSlice, createAsyncThunk, type PayloadAction } from '@reduxjs/toolkit';
import { getDeviceId } from 'helpers/StorageHelper';

import { getHeaderFooterConfig, getLiveUpdates } from '../actions/CustomerHomePageAction';

export interface CustomerGlobalState {
  headerFooterData: any | null;
  loadingHeaderFooterData: boolean;
  appTheme: 'dark' | 'light';
  // header live ticker feed (newest products / FFLs / discounts / coupons)
  live_updates: any[];
  categories: any[];
  payment_types: any[];
  socialMediaUrls: any;
  total_gun_for_sale: number;
  total_followed_ffl: number;
  unread_notification_count: number;
  support_email: string;
  // address chosen in the header "Deliver to" block, shared with every place
  // that shows the selected shipping FFL (profile dropdown, mobile drawer)
  selectedDeliveryAddress: any | null;
  // "Select Delivery Location" modal is mounted once in the header and can be
  // opened from anywhere that shows the selected address / FFL
  deliveryLocationModalOpen: boolean;
  // logged-out counterpart: guests have no address book, so the header "Deliver to"
  // block opens the FFL map picker instead and stores the dealer as the location
  guestDeliveryFFLModalOpen: boolean;
  // the mobile burger menu drawer (rendered by the top header, opened from the top header
  // and from the bottom bar)
  mobileMenuOpen: boolean;
}

const initialState: CustomerGlobalState = {
  headerFooterData: null,
  loadingHeaderFooterData: true,
  appTheme: (localStorage.getItem('app-theme') as 'dark' | 'light') || 'dark',
  live_updates: [],
  categories: [],
  payment_types: [],
  socialMediaUrls: {},
  total_gun_for_sale: 0,
  total_followed_ffl: 0,
  unread_notification_count: 0,
  support_email: '',
  selectedDeliveryAddress: null,
  deliveryLocationModalOpen: false,
  guestDeliveryFFLModalOpen: false,
  mobileMenuOpen: false,
};

export const fetchHeaderFooterData = createAsyncThunk(
  'customerGlobal/fetchHeaderFooterData',
  async () => {
    const response: any = await getHeaderFooterConfig({
      session_id: getDeviceId(),
    });
    // const response: any = await getHomepageConfigs({
    //   latlong: { lat: 40.7128, long: -74.006, radius: 2000000 },
    // });
    if (response?.status) return response.data;
    return null;
  }
);

export const fetchLiveUpdates = createAsyncThunk(
  'customerGlobal/fetchLiveUpdates',
  async (limit: number) => {
    const response: any = await getLiveUpdates({ limit });
    if (response?.status) return response.data?.live_updates || [];
    return [];
  }
);

const customerGlobalSlice = createSlice({
  name: 'customerGlobal',
  initialState,
  reducers: {
    setHeaderFooterData(state, action: PayloadAction<CustomerGlobalState['headerFooterData']>) {
      state.headerFooterData = action.payload;
    },
    setSelectedDeliveryAddress(
      state,
      action: PayloadAction<CustomerGlobalState['selectedDeliveryAddress']>
    ) {
      state.selectedDeliveryAddress = action.payload;
    },
    setDeliveryLocationModalOpen(state, action: PayloadAction<boolean>) {
      state.deliveryLocationModalOpen = action.payload;
    },
    setGuestDeliveryFFLModalOpen(state, action: PayloadAction<boolean>) {
      state.guestDeliveryFFLModalOpen = action.payload;
    },
    setMobileMenuOpen(state, action: PayloadAction<boolean>) {
      state.mobileMenuOpen = action.payload;
    },
    setAppTheme(state, action: PayloadAction<'dark' | 'light'>) {
      state.appTheme = action.payload;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchHeaderFooterData.pending, (state) => {
        state.loadingHeaderFooterData = true;
      })
      .addCase(fetchHeaderFooterData.fulfilled, (state, action) => {
        const data = action.payload;
        state.headerFooterData = data;
        state.loadingHeaderFooterData = false;
        // store gun for sale count
        state.total_gun_for_sale = data?.total_gun_for_sale || 0;
        // following
        state.total_followed_ffl = data?.total_followed_ffl || 0;
        // notification
        state.unread_notification_count = data?.unread_notification_count || 0;
        // store categories
        state.categories = data?.categories || [];
        // store social links
        state.socialMediaUrls = data?.social_media_urls || {};
        // payment types
        state.payment_types = data?.payment_types || [];
        // support email
        state.support_email = data?.support_email || '';
      })
      .addCase(fetchHeaderFooterData.rejected, (state) => {
        state.loadingHeaderFooterData = false;
      })
      // a failed poll keeps the previously loaded ticker items on screen
      .addCase(fetchLiveUpdates.fulfilled, (state, action) => {
        state.live_updates = action.payload || [];
      });
  },
});

export const {
  setHeaderFooterData,
  setSelectedDeliveryAddress,
  setDeliveryLocationModalOpen,
  setGuestDeliveryFFLModalOpen,
  setMobileMenuOpen,
  setAppTheme,
} = customerGlobalSlice.actions;
export default customerGlobalSlice.reducer;
